import { useState } from 'react';
import { Alert, Table } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { IOpoContact, IOpoContactRequest } from '@nkr/models/opo-contact';
import useOpoContacts from '@nkr/store/opo/hooks/use-opo-contacts';
import { SettingsPageWrapper } from '../styles';
import { Pill, SettingsTableCard } from '../table-styles';
import ConfirmModal from '../confirm-modal';
import ContactModal from './contact-modal';
import { otherPositionId } from './contact-role-checkboxes';

const OpoContacts = () => {
    const { data, loading, error, save, remove } = useOpoContacts();
    const [editing, setEditing] = useState<{ show: boolean, contact: IOpoContact | null }>({ show: false, contact: null });
    const [deleting, setDeleting] = useState<IOpoContact | null>(null);
    const [actionError, setActionError] = useState('');

    if (loading) {
        return null;
    }

    const canManage = data?.canManage == true;
    const assignedPositions = data ? data.contacts.reduce((ids, x) => ids.concat(x.positions.map(p => p.id)), [] as number[]) : [];
    const missingPositions = data ? data.positions.filter(x => x.id != otherPositionId && assignedPositions.indexOf(x.id) < 0).map(x => x.description) : [];

    const handleSave = async (contactId: number | null, request: IOpoContactRequest) => {
        const result = await save(contactId, request);

        if (result.success) {
            setEditing({ show: false, contact: null });
            toast.success('Contact saved');
        }

        return result;
    };

    const handleDelete = () => {
        const contact = deleting;

        setActionError('');
        setDeleting(null);

        remove(contact.id).then(result => {
            if (result.success) {
                toast.success(`${contact.firstName} ${contact.lastName} deleted`);
            } else {
                setActionError(result.message);
            }
        });
    };

    return (
        <SettingsPageWrapper style={{ maxWidth: 1100 }}>
            {(error || actionError) && (
                <Alert bsStyle='danger' onDismiss={actionError ? () => setActionError('') : undefined}>{error || actionError}</Alert>
            )}

            {data && !canManage && (
                <div className='settings-notice'>Only OPO administrators can add or change contacts.</div>
            )}

            {data && (
                <SettingsTableCard>
                    <div className='table-card-header'>
                        <h4>Contacts</h4>
                        {missingPositions.length > 0 && (
                            <span className='count'>Unassigned roles: {missingPositions.join(', ')}</span>
                        )}
                        {canManage && (
                            <button type='button' className='btn btn-primary' onClick={() => setEditing({ show: true, contact: null })}>Add Contact</button>
                        )}
                    </div>

                    <div className='table-card-body'>
                        <Table condensed>
                            <thead>
                                <tr>
                                    <th style={{ textAlign: 'left' }}>Name</th>
                                    <th style={{ textAlign: 'left' }}>Roles</th>
                                    <th style={{ textAlign: 'left' }}>Email</th>
                                    <th style={{ textAlign: 'left' }}>Phone</th>
                                    <th></th>
                                </tr>
                            </thead>
                            <tbody>
                                {data.contacts.map(contact =>
                                    <tr key={contact.id}>
                                        <td style={{ textAlign: 'left' }}>
                                            <div>
                                                {contact.firstName} {contact.lastName}
                                                {contact.isPortalUser && (
                                                    <>{' '}<Pill $color='blue'>Portal User</Pill></>
                                                )}
                                            </div>
                                            {contact.title && <div className='muted'>{contact.title}</div>}
                                        </td>
                                        <td style={{ textAlign: 'left', whiteSpace: 'normal' }}>
                                            {contact.positions.length > 0
                                                ? contact.positions.map(x => x.description).join(', ')
                                                : <span className='muted'>None</span>}
                                        </td>
                                        <td style={{ textAlign: 'left' }}>
                                            <a href={`mailto:${contact.email}`}>{contact.email}</a>
                                        </td>
                                        <td style={{ textAlign: 'left' }}>
                                            {contact.officePhone && <div>{contact.officePhone} <span className='muted'>(office)</span></div>}
                                            {contact.mobilePhone && <div>{contact.mobilePhone} <span className='muted'>(mobile)</span></div>}
                                        </td>
                                        <td>
                                            {canManage && (
                                                <div className='btn-group'>
                                                    <button type='button' className='btn btn-default' onClick={() => setEditing({ show: true, contact })}>Edit</button>
                                                    <button type='button' className='btn btn-default' disabled={contact.isPortalUser} title={contact.isPortalUser ? 'Deactivate this user from Portal Users instead' : undefined} onClick={() => setDeleting(contact)}>Delete</button>
                                                </div>
                                            )}
                                        </td>
                                    </tr>
                                )}

                                {data.contacts.length == 0 && (
                                    <tr>
                                        <td colSpan={5}>No contacts have been added yet</td>
                                    </tr>
                                )}
                            </tbody>
                        </Table>
                    </div>
                </SettingsTableCard>
            )}

            {data && (
                <ContactModal
                    show={editing.show}
                    contact={editing.contact}
                    positions={data.positions}
                    onSave={handleSave}
                    onCancel={() => setEditing({ show: false, contact: null })}
                />
            )}

            <ConfirmModal
                show={deleting != null}
                title='Delete Contact'
                message={<>Delete <strong>{deleting?.firstName} {deleting?.lastName}</strong> ({deleting?.email})?</>}
                confirmText='Delete'
                onConfirm={handleDelete}
                onCancel={() => setDeleting(null)}
            />
        </SettingsPageWrapper>
    );
}

export default OpoContacts;
